import React from 'react';
import { motion } from 'framer-motion';
import type { ConsiderationId } from '../../data/caprelsa';
const EASE = [0.23, 1, 0.32, 1] as const;
const PURPLE = '#3d2785';
const GOLD = '#d6a530';
const ORANGE = '#e5862d';
const MONO = 'IBM Plex Mono, monospace';

/** Small animated cue for each clinical consideration in the Interact scene. */
export function ConsiderationVisual({
  id
}: {
  id: ConsiderationId;
}) {
  if (id === 'progression') {
    return <svg viewBox="0 0 300 74" className="h-[74px] w-full" aria-hidden="true" data-id="element-432" data-mp="25bteyoop0pkg">
        {[18, 37, 56].map(y => <line key={y} x1="0" x2="300" y1={y} y2={y} stroke={PURPLE} strokeOpacity="0.08" data-id="element-433" data-mp="2f7wseqozwamp" />)}
        <motion.path d="M4 66 C 60 64, 110 58, 150 48 S 240 22, 296 6" fill="none" stroke={ORANGE} strokeWidth="2.4" initial={{
        pathLength: 0
      }} animate={{
        pathLength: 1
      }} transition={{
        duration: 1.1,
        ease: EASE
      }} data-id="element-434" data-mp="2a8tnd690m4eq" />
        <motion.path d="M4 68 C 70 66, 130 62, 170 54 S 250 34, 296 22" fill="none" stroke={PURPLE} strokeWidth="1.8" strokeDasharray="3 3" initial={{
        pathLength: 0
      }} animate={{
        pathLength: 1
      }} transition={{
        duration: 1.1,
        delay: 0.15,
        ease: EASE
      }} data-id="element-435" data-mp="239a52qxxhevr" />
        <text x="4" y="12" fill={ORANGE} fontSize="9" fontWeight="600" fontFamily={MONO} data-id="element-436" data-mp="2h81h87t8cqh9">CALCITONIN</text>
        <text x="90" y="12" fill={PURPLE} fontSize="9" fontWeight="600" fontFamily={MONO} data-id="element-437" data-mp="1kk3hoqm6vz3m">CEA</text>
        <text x="296" y="72" textAnchor="end" fill={PURPLE} fillOpacity="0.45" fontSize="8" fontFamily={MONO} data-id="element-438" data-mp="1pioios99mmt4">TIME →</text>
      </svg>;
  }
  if (id === 'ret') {
    return <div className="flex h-[74px] flex-col justify-center" aria-hidden="true" data-id="element-439" data-mp="1yagp8rv65bsf">
        <div className="relative h-5 w-full overflow-hidden rounded-full bg-cap-lilac ring-1 ring-cap-line" data-id="element-440" data-mp="11skdwhkcch0j">
          {Array.from({
          length: 18
        }).map((_, i) => <span key={i} className="absolute top-0 h-full w-[2px] bg-cap-purple/10" style={{
          left: `${(i + 1) * 5.2}%`
        }} data-id="element-441" data-mp="15rt9lryxyoyq" data-mpi={i} />)}
          <motion.span className="absolute top-0 h-full rounded-full bg-cap-gold" style={{
          left: '58%'
        }} initial={{
          width: 0
        }} animate={{
          width: '9%'
        }} transition={{
          duration: 0.6,
          delay: 0.2,
          ease: EASE
        }} data-id="element-442" data-mp="2468kt4kddgoh" />
        </div>
        <div className="mt-2 flex items-center justify-between font-mono text-[9px] tracking-[0.14em] text-cap-grey" data-id="element-443" data-mp="1q5pcrxfwmamb">
          <span data-id="element-444" data-mp="1l7nydptfazh6">CHROMOSOME 10</span>
          <span className="font-semibold text-cap-purple" data-id="element-445" data-mp="1l8p6pmj217v7">RET · 10q11.2</span>
        </div>
        <div className="mt-2 flex gap-1.5" data-id="element-446" data-mp="1z871cij17fch">
          {['Germline', 'Somatic'].map((t, i) => <motion.span key={t} initial={{
          opacity: 0,
          y: 4
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.45 + i * 0.1
        }} className="rounded-full border border-cap-purple/25 px-2 py-0.5 text-[10px] font-semibold text-cap-purple" data-id="element-447" data-mp="1v3h7ez7xoau7" data-mpi={i}>
              {t}
            </motion.span>)}
        </div>
      </div>;
  }
  return <svg viewBox="0 0 300 74" className="h-[74px] w-full" aria-hidden="true" data-id="element-448" data-mp="3j0gla0bh0ztf">
      <line x1="0" x2="300" y1="46" y2="46" stroke={PURPLE} strokeOpacity="0.1" data-id="element-449" data-mp="347ddgknom0fw" />
      <motion.path d="M0 46 L40 46 L48 40 L56 46 L70 46 L76 52 L82 10 L88 60 L94 46 L120 46 C 130 46, 136 28, 150 28 C 164 28, 168 46, 178 46 L300 46" fill="none" stroke={PURPLE} strokeWidth="2" initial={{
      pathLength: 0
    }} animate={{
      pathLength: 1
    }} transition={{
      duration: 1.2,
      ease: EASE
    }} data-id="element-450" data-mp="395exp8wug5sy" />
      <motion.g initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 1
    }} data-id="element-451" data-mp="3s37zg7vqe9or">
        <rect x="76" y="64" width="102" height="1.5" fill={GOLD} data-id="element-452" data-mp="jno7dc6sqxba" />
        <rect x="76" y="60" width="1.5" height="9" fill={GOLD} data-id="element-453" data-mp="jmd7vokdwbpz" />
        <rect x="176.5" y="60" width="1.5" height="9" fill={GOLD} data-id="element-454" data-mp="ofg2m00wdr7r" />
        <text x="127" y="12" textAnchor="middle" fill={ORANGE} fontSize="9" fontWeight="600" fontFamily={MONO} letterSpacing="1" data-id="element-455" data-mp="2wv7e65qnplff">
          QTc INTERVAL
        </text>
      </motion.g>
    </svg>;
}